import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { toast, Toaster } from "sonner";
import { BarChart3, TrendingUp, Receipt, Wallet } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";

const CATEGORY_LABELS = {
  golf: "Golf",
  promotion: "Promotion",
  representation_compagnie: "Représentation · Compagnie",
  representation_repas: "Représentation · Repas",
  association: "Association",
  hotel_avion: "Hôtel / Avion",
  auto_essence: "Auto / Essence",
  stationnement: "Stationnement / Taxi",
  salon_congres: "Salon / Congrès",
  bureau: "Bureau",
  informatique: "Informatique",
  communication: "Communication",
  formation: "Formation",
};

const PIE_COLORS = ["#002FA7", "#4F46E5", "#0891B2", "#059669", "#65A30D", "#CA8A04", "#DC2626", "#DB2777", "#7C3AED", "#6366F1", "#0EA5E9", "#10B981", "#F59E0B"];

const REPORT_STATUS_LABELS = {
  active: "Brouillon",
  ready_for_review: "En attente",
  approved: "Validé",
  rejected: "Refusé",
  archived: "Archivé",
};

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = month ? { month } : {};
      const { data } = await api.get("/stats", { params });
      setStats(data);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const categoryChartData = useMemo(() => {
    return (stats?.by_category || []).map((c) => ({
      name: CATEGORY_LABELS[c.category] || c.category,
      total: c.total,
      count: c.count,
    })).slice(0, 10);
  }, [stats]);

  const monthChartData = useMemo(() => {
    return [...(stats?.by_month || [])].reverse();
  }, [stats]);

  const reportStatusData = useMemo(() => {
    const entries = Object.entries(stats?.reports_by_status || {});
    return entries.map(([k, v]) => ({ name: REPORT_STATUS_LABELS[k] || k, value: v, key: k }));
  }, [stats]);

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Mes statistiques</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none">
            Vos dépenses en un coup d'œil.
          </h1>
          <p className="text-gray-500 mt-3 max-w-lg">Répartition par catégorie, évolution mensuelle, provinces et statuts de rapports.</p>
        </div>
        <div>
          <label className="ra-label">Mois</label>
          <input
            type="month"
            className="ra-input !min-w-[180px]"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="stats-month-filter"
          />
        </div>
      </div>

      {loading && <div className="progress-line mb-6" />}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KpiCard label="Total dépenses" value={`${(stats?.global?.total ?? 0).toFixed(2)} $`} icon={Wallet} testId="kpi-total" />
        <KpiCard label="Factures" value={stats?.global?.count ?? 0} icon={Receipt} testId="kpi-count" />
        <KpiCard label="TPS + TVQ + TVH" value={`${((stats?.global?.tps ?? 0) + (stats?.global?.tvq ?? 0) + (stats?.global?.tvh ?? 0)).toFixed(2)} $`} icon={TrendingUp} testId="kpi-taxes" />
        <KpiCard label="Pourboires" value={`${(stats?.global?.tip ?? 0).toFixed(2)} $`} icon={BarChart3} testId="kpi-tip" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Monthly evolution */}
        <div className="ra-card p-5" data-testid="my-stats-monthly">
          <div className="overline mb-1">Évolution mensuelle</div>
          <div className="font-heading font-black text-lg tracking-tight mb-4">Par mois</div>
          {monthChartData.length === 0 ? (
            <EmptyState msg="Aucune donnée" />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={monthChartData}>
                <defs>
                  <linearGradient id="colorMy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#002FA7" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#002FA7" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}$`} />
                <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} $`, "Total"]} />
                <Area type="monotone" dataKey="total" stroke="#002FA7" strokeWidth={2} fill="url(#colorMy)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Reports status donut */}
        <div className="ra-card p-5" data-testid="my-stats-reports">
          <div className="overline mb-1">Mes rapports</div>
          <div className="font-heading font-black text-lg tracking-tight mb-4">Par statut</div>
          {reportStatusData.length === 0 ? (
            <EmptyState msg="Aucun rapport" />
          ) : (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={reportStatusData} dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {reportStatusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <ul className="space-y-2 text-sm flex-1">
                {reportStatusData.map((s, i) => (
                  <li key={s.key} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-gray-600">{s.name}</span>
                    <span className="ml-auto num font-semibold">{s.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* By category horizontal bars */}
      <div className="ra-card p-5 mb-8" data-testid="my-stats-category">
        <div className="overline mb-1">Répartition par catégorie</div>
        <div className="font-heading font-black text-lg tracking-tight mb-4">Top 10</div>
        {categoryChartData.length === 0 ? (
          <EmptyState msg="Aucune catégorie" />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, categoryChartData.length * 34)}>
            <BarChart data={categoryChartData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}$`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={160} />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} $`, "Total"]} />
              <Bar dataKey="total" fill="#002FA7" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* By province */}
      {(stats?.by_province?.length ?? 0) > 0 && (
        <div className="ra-card p-5 mb-8" data-testid="my-stats-province">
          <div className="overline mb-1">Par province / lieu</div>
          <div className="font-heading font-black text-lg tracking-tight mb-4">{stats.by_province.length} lieu(x)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {stats.by_province.map((p) => (
              <div key={p.province} className="border border-gray-200 dark:border-neutral-700 p-3 rounded">
                <div className="text-xs text-gray-500 uppercase tracking-wider">{p.province}</div>
                <div className="font-heading font-black text-xl num">{p.total.toFixed(2)} $</div>
                <div className="text-xs text-gray-400">{p.count} facture(s)</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}

function KpiCard({ label, value, icon: Icon, testId }) {
  return (
    <div className="ra-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">{label}</div>
        {Icon && <Icon size={16} className="text-gray-300" />}
      </div>
      <div className="font-heading font-black text-2xl tracking-tighter num">{value}</div>
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="text-center text-gray-400 py-12 text-sm">{msg}</div>
  );
}
